import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { set } from 'lodash-es';
import { flushSync } from 'react-dom';
import { v4 as uuidv4 } from 'uuid';
import { useNotifications } from 'hooks/useNotifications';
import { IContentEntity, makeBackendApi } from 'services/BackendApi';
import {
  IMarketingManagerOpportunityRequest,
  IMarketingManagerImageUpload,
  IMarketingManagerFileUpload,
  IMarketingManagerOpportunityUpdateRequest,
} from 'services/MarketingManagerApi';
import { IContentSection } from '../types';
import { useContentUploader } from './useContentUploader';
import { useCreateOpportunity, useOpportunity, useUpdateOpportunity } from './useContentMarketingQueries';
import { isValidEmail } from 'utils/email';
import { parseAsUTC } from 'utils/date.utc';
import { isBlank, isBlankHtml } from 'utils';

// Use the API type but make heroImage optional and override sections for UI state management
interface IContentEditState extends Omit<IMarketingManagerOpportunityRequest, 'heroImage' | 'sections'> {
  heroImage?: IMarketingManagerImageUpload; // Optional in state until uploaded
  sections?: IContentSection[]; // Override with local sections type that includes id
}

interface IValidationErrors {
  title?: boolean;
  crmCompanyUuid?: boolean;
  categoryUuids?: boolean;
  shortDescriptionTitle?: boolean;
  shortDescriptionContent?: boolean;
  contactEmail?: boolean;
  heroImage?: boolean;
  activeFrom?: boolean;
}

interface IUseContentEditProps {
  contentUuid: string | null; // null for create mode
}

export const useContentEdit = ({ contentUuid }: IUseContentEditProps) => {
  const history = useHistory();
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const backendApi = makeBackendApi();

  // Only use contentUploader if we have a contentUuid (edit mode)
  const contentUploader = contentUuid ? useContentUploader(contentUuid) : null;
  const createOpportunityMutation = useCreateOpportunity();
  const updateOpportunityMutation = useUpdateOpportunity();

  // Fetch existing opportunity data if in edit mode
  const { data: existingOpportunity, isLoading: isLoadingOpportunity, error: opportunityError } = useOpportunity(
    contentUuid
  );

  // State management - using API type structure
  const [contentEditState, setContentEditState] = useState<IContentEditState>({
    // heroImage starts undefined until uploaded
    title: '',
    crmCompanyUuid: '',
    categoryUuids: [], // Array for multiple categories
    shortDescription: {
      title: '',
      content: '',
    },
    publishFor: 'all_users',
    // Optional fields with defaults
    destinations: [],
    productName: '',
    productTypeUuids: [],
    isExclusive: false,
    sections: [],
    countryCodes: [],
    bookingStart: '',
    bookingEnd: '',
    stayPeriodStart: '',
    stayPeriodEnd: '',
    currency: '',
    fullPrice: undefined,
    discountedPrice: undefined,
    discountPercent: undefined,
    photos: [],
    documents: [],
    saveAsDraft: true,
    activeFrom: '',
    activeTo: '',
    contactEmail: '',
    categoriesHidden: false, // Default: categories are visible (toggle ON)
  });

  const [isEditedWithoutSaving, setIsEditedWithoutSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<IValidationErrors>({});

  // Initialize state with existing opportunity data (for edit mode)
  useEffect(() => {
    if (existingOpportunity && contentUuid) {
      // Transform existing opportunity data to match our local state structure
      const transformedSections =
        existingOpportunity.sections?.map((section, index) => ({
          ...section,
          id: `section-${index}`, // Add ID for UI management
        })) || [];

      setContentEditState({
        heroImage: existingOpportunity.heroImage,
        title: existingOpportunity.title || '',
        crmCompanyUuid: existingOpportunity.crmCompanyUuid || '',
        categoryUuids: existingOpportunity.categoryUuids || [],
        shortDescription: existingOpportunity.shortDescription || { title: '', content: '' },
        publishFor:
          (existingOpportunity.publishFor as 'all_users' | 'internal_users_only' | 'external_users_only') ||
          'all_users',
        destinations: existingOpportunity.destinations || [],
        productName: existingOpportunity.productName || '',
        productTypeUuids: existingOpportunity.productTypeUuids || [],
        isExclusive: existingOpportunity.isExclusive || false,
        sections: transformedSections,
        countryCodes: existingOpportunity.countryCodes || [],
        bookingStart: existingOpportunity.bookingStart || '',
        bookingEnd: existingOpportunity.bookingEnd || '',
        stayPeriodStart: existingOpportunity.stayPeriodStart || '',
        stayPeriodEnd: existingOpportunity.stayPeriodEnd || '',
        currency: existingOpportunity.currency || '',
        fullPrice: existingOpportunity.fullPrice,
        discountedPrice: existingOpportunity.discountedPrice,
        discountPercent: existingOpportunity.discountPercent,
        photos: existingOpportunity.photos || [],
        documents: existingOpportunity.documents || [],
        saveAsDraft: existingOpportunity.saveAsDraft !== false, // Default to true if not explicitly false
        activeFrom: existingOpportunity.activeFrom || '',
        activeTo: existingOpportunity.activeTo || '',
        contactEmail: existingOpportunity.contactEmail || '',
        categoriesHidden: existingOpportunity.categoriesHidden ?? false, // Default: categories are visible
      });

      // Don't mark as edited when initializing with existing data
      setIsEditedWithoutSaving(false);
    }
  }, [existingOpportunity, contentUuid]);

  // Helper to clear individual validation error
  const clearValidationError = useCallback((field: keyof IValidationErrors) => {
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  // Generic update function using lodash set
  const updateContentData = useCallback((path: string, value: any) => {
    setContentEditState(prevState => {
      const newState = { ...prevState };
      set(newState, path, value);
      return newState;
    });
    setIsEditedWithoutSaving(true);
  }, []);

  // Helper function to clean payload for API submission
  const cleanPayloadForApi = useCallback((data: IContentEditState): IMarketingManagerOpportunityRequest => {
    const cleanData: any = {};

    // Always include required fields (even if empty, for validation)
    cleanData.title = data.title;
    cleanData.crmCompanyUuid = data.crmCompanyUuid;
    cleanData.categoryUuids = data.categoryUuids;
    cleanData.publishFor = data.publishFor;

    // Always send categoriesHidden
    cleanData.categoriesHidden = data.categoriesHidden ?? false; // Default: categories are visible

    // Include heroImage only if it exists
    if (data.heroImage) {
      cleanData.heroImage = data.heroImage;
    }

    // Handle shortDescription - required but needs to check nested fields
    // Trim whitespace from both title and content
    cleanData.shortDescription = {
      title: (data.shortDescription.title || '').trim(),
      content: (data.shortDescription.content || '').trim(),
    };

    // Always send optional fields with explicit null/empty values to allow un-setting

    // Arrays - always send (empty array if no items)
    cleanData.destinations = data.destinations || [];
    cleanData.productTypeUuids = data.productTypeUuids || [];
    cleanData.countryCodes = data.countryCodes || [];
    cleanData.photos = data.photos || [];
    cleanData.documents = data.documents || [];

    // Sections - always send array, filter out truly empty sections
    if (data.sections && data.sections.length > 0) {
      const nonEmptySections = data.sections.filter(section => section.title.trim() || section.content.trim());
      cleanData.sections = nonEmptySections.map(section => ({
        title: section.title,
        content: section.content,
      }));
    } else {
      cleanData.sections = [];
    }

    // Strings - send null if empty
    cleanData.productName = data.productName && data.productName.trim() ? data.productName : null;
    cleanData.currency = data.currency && data.currency.trim() ? data.currency : null;
    cleanData.contactEmail = data.contactEmail && data.contactEmail.trim() ? data.contactEmail : null;

    // Booleans - always send
    cleanData.isExclusive = data.isExclusive ?? false;
    cleanData.saveAsDraft = data.saveAsDraft ?? false;

    // Date fields - send null if empty
    cleanData.bookingStart = data.bookingStart && data.bookingStart.trim() ? parseAsUTC(data.bookingStart) : null;
    cleanData.bookingEnd = data.bookingEnd && data.bookingEnd.trim() ? parseAsUTC(data.bookingEnd) : null;
    cleanData.stayPeriodStart =
      data.stayPeriodStart && data.stayPeriodStart.trim() ? parseAsUTC(data.stayPeriodStart) : null;
    cleanData.stayPeriodEnd = data.stayPeriodEnd && data.stayPeriodEnd.trim() ? parseAsUTC(data.stayPeriodEnd) : null;
    cleanData.activeFrom = data.activeFrom && data.activeFrom.trim() ? parseAsUTC(data.activeFrom) : null;
    cleanData.activeTo = data.activeTo && data.activeTo.trim() ? parseAsUTC(data.activeTo) : null;

    // Pricing fields - send null if empty/undefined
    cleanData.fullPrice = data.fullPrice ?? null;
    cleanData.discountedPrice = data.discountedPrice ?? null;
    cleanData.discountPercent = data.discountPercent ?? null;

    return cleanData as IMarketingManagerOpportunityRequest;
  }, []);

  // Save function
  const handleSave = useCallback(async () => {
    // Validate required fields and set validation errors
    const newValidationErrors: IValidationErrors = {};

    if (!contentEditState.title.trim()) {
      newValidationErrors.title = true;
    }
    if (!contentEditState.crmCompanyUuid.trim()) {
      newValidationErrors.crmCompanyUuid = true;
    }
    if (!contentEditState.categoryUuids || contentEditState.categoryUuids.length === 0) {
      newValidationErrors.categoryUuids = true;
    }
    if (isBlank(contentEditState.shortDescription.title.trim())) {
      newValidationErrors.shortDescriptionTitle = true;
    }
    if (isBlankHtml(contentEditState.shortDescription.content)) {
      newValidationErrors.shortDescriptionContent = true;
    }
    // Hero image is mandatory ONLY when editing (not when creating)
    if (contentUuid && !contentEditState.heroImage) {
      newValidationErrors.heroImage = true;
    }
    if (
      contentEditState.contactEmail &&
      contentEditState.contactEmail.trim() &&
      !isValidEmail(contentEditState.contactEmail.trim())
    ) {
      newValidationErrors.contactEmail = true;
    }
    // Active From is mandatory when saveAsDraft is false
    if (!contentEditState.saveAsDraft && !contentEditState.activeFrom?.trim()) {
      newValidationErrors.activeFrom = true;
    }

    if (Object.keys(newValidationErrors).length > 0) {
      setValidationErrors(newValidationErrors);

      // Scroll to the first validation error field
      const firstErrorField = Object.keys(newValidationErrors)[0];
      const element = document.querySelector(`[data-validation-id="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (contentUuid) {
        // Update existing opportunity
        const updateData: IMarketingManagerOpportunityUpdateRequest = cleanPayloadForApi(contentEditState);
        await updateOpportunityMutation.mutateAsync({
          uuid: contentUuid,
          opportunityData: updateData,
        });
        showSuccessNotification('Content updated successfully');
        // Redirect to content list after successful update
        history.push('/content');
      } else {
        // Create new opportunity using the Marketing Manager API
        // Clean payload to exclude empty/null values
        const opportunityData = cleanPayloadForApi(contentEditState);

        const response = await createOpportunityMutation.mutateAsync(opportunityData);
        showSuccessNotification('Content created successfully');

        // Redirect to edit page with the new content UUID
        if (response.uuid) {
          history.push(`/content/edit/${response.uuid}`);
        } else {
          // Fallback to content list if no UUID returned
          history.push('/content');
        }
      }

      setIsEditedWithoutSaving(false);
    } catch (err) {
      setError(err.message || 'Failed to save content');
      showErrorNotification('Failed to save content');
    } finally {
      setIsLoading(false);
    }
  }, [
    contentEditState,
    contentUuid,
    isEditedWithoutSaving,
    createOpportunityMutation,
    updateOpportunityMutation,
    showSuccessNotification,
    showErrorNotification,
    history,
    cleanPayloadForApi,
  ]);

  // Cancel function
  const handleCancel = useCallback(() => {
    if (isEditedWithoutSaving) {
      // LeaveWithoutSavingModal will handle this
      return;
    }
    history.push('/content');
  }, [isEditedWithoutSaving, history]);

  // Specific update functions for API fields
  const updateTitle = useCallback(
    (title: string) => {
      updateContentData('title', title);
      if (title.trim()) {
        clearValidationError('title');
      }
    },
    [updateContentData, clearValidationError]
  );

  // Main Info section update functions
  const updateCrmCompanyUuid = useCallback(
    (crmCompanyUuid: string | null) => {
      updateContentData('crmCompanyUuid', crmCompanyUuid || '');
      if (crmCompanyUuid && crmCompanyUuid.trim()) {
        clearValidationError('crmCompanyUuid');
      }
    },
    [updateContentData, clearValidationError]
  );

  const updateCategoryUuid = useCallback(
    (categoryUuids: string[]) => {
      updateContentData('categoryUuids', categoryUuids);
      if (categoryUuids && categoryUuids.length > 0) {
        clearValidationError('categoryUuids');
      }
    },
    [updateContentData, clearValidationError]
  );

  const updateCategoryEnabled = useCallback(
    (enabled: boolean) => {
      updateContentData('categoriesHidden', !enabled); // INVERSE: enabled=true means hidden=false
    },
    [updateContentData]
  );

  const updateDestination = useCallback(
    (destinations: string[]) => {
      updateContentData('destinations', destinations);
    },
    [updateContentData]
  );

  const updateProductName = useCallback(
    (productName: string) => {
      updateContentData('productName', productName);
    },
    [updateContentData]
  );

  const updateIsExclusive = useCallback(
    (isExclusive: boolean) => {
      updateContentData('isExclusive', isExclusive);
    },
    [updateContentData]
  );

  const updateProductTypeUuids = useCallback(
    (productTypeUuids: string[]) => {
      updateContentData('productTypeUuids', productTypeUuids);
    },
    [updateContentData]
  );

  const updateHeroImage = useCallback(
    (heroImage: IMarketingManagerImageUpload | undefined) => {
      updateContentData('heroImage', heroImage);
      if (heroImage) {
        clearValidationError('heroImage');
      }
    },
    [updateContentData, clearValidationError]
  );

  // Hero image upload function
  const addHeroImage = useCallback(
    async (file: File, displayName: string) => {
      if (!contentUploader) {
        showErrorNotification('Please save the content item before adding uploads');
        throw new Error('ContentUploader not available in create mode');
      }

      try {
        const uploadResult = await contentUploader.uploadAndProcessFile({
          file,
          displayName,
          fileType: 'image',
        });

        if (uploadResult) {
          // uploadResult should contain the processed upload data from the backend
          updateHeroImage(uploadResult as IMarketingManagerImageUpload);
          showSuccessNotification('Hero image uploaded successfully');
        } else {
          showErrorNotification('Failed to upload hero image');
        }
      } catch (error) {
        showErrorNotification('Failed to upload hero image');
      }
    },
    [contentUploader, updateHeroImage, showSuccessNotification, showErrorNotification]
  );

  // Content Details section update functions
  const updateShortDescriptionTitle = useCallback(
    (title: string) => {
      updateContentData('shortDescription.title', title);
      if (title.trim()) {
        clearValidationError('shortDescriptionTitle');
      }
    },
    [updateContentData, clearValidationError]
  );

  const updateShortDescriptionContent = useCallback(
    (content: string) => {
      updateContentData('shortDescription.content', content);
      if (!isBlankHtml(content)) {
        clearValidationError('shortDescriptionContent');
      }
    },
    [updateContentData, clearValidationError]
  );

  const addSection = useCallback(() => {
    const newSection: IContentSection = {
      id: `section-${Date.now()}`,
      title: '',
      content: '',
    };
    updateContentData('sections', [...(contentEditState.sections || []), newSection]);
  }, [updateContentData, contentEditState.sections]);

  const removeSection = useCallback(
    (sectionId: string) => {
      const updatedSections = (contentEditState.sections || []).filter(section => section.id !== sectionId);
      updateContentData('sections', updatedSections);
    },
    [updateContentData, contentEditState.sections]
  );

  const updateSectionTitle = useCallback(
    (sectionId: string, title: string) => {
      const updatedSections = (contentEditState.sections || []).map(section =>
        section.id === sectionId ? { ...section, title } : section
      );
      updateContentData('sections', updatedSections);
    },
    [updateContentData, contentEditState.sections]
  );

  const updateSectionContent = useCallback(
    (sectionId: string, content: string) => {
      const updatedSections = (contentEditState.sections || []).map(section =>
        section.id === sectionId ? { ...section, content } : section
      );
      updateContentData('sections', updatedSections);
    },
    [updateContentData, contentEditState.sections]
  );

  // Availability & Booking section update functions
  const updateCountryCodes = useCallback(
    (countryCodes: string[]) => {
      updateContentData('countryCodes', countryCodes);
    },
    [updateContentData]
  );

  const resetCountryCodes = useCallback(() => {
    updateContentData('countryCodes', []);
  }, [updateContentData]);

  const updateBookingStart = useCallback(
    (bookingStart: string) => {
      updateContentData('bookingStart', bookingStart);
    },
    [updateContentData]
  );

  const updateBookingEnd = useCallback(
    (bookingEnd: string) => {
      updateContentData('bookingEnd', bookingEnd);
    },
    [updateContentData]
  );

  const updateStayPeriodStart = useCallback(
    (stayPeriodStart: string) => {
      updateContentData('stayPeriodStart', stayPeriodStart);
    },
    [updateContentData]
  );

  const updateStayPeriodEnd = useCallback(
    (stayPeriodEnd: string) => {
      updateContentData('stayPeriodEnd', stayPeriodEnd);
    },
    [updateContentData]
  );

  // Pricing Info section update functions
  const updateCurrency = useCallback(
    (currency: string) => {
      updateContentData('currency', currency);
    },
    [updateContentData]
  );

  const updateFullPrice = useCallback(
    (fullPrice: number | null) => {
      updateContentData('fullPrice', fullPrice);
    },
    [updateContentData]
  );

  const updateDiscountedPrice = useCallback(
    (discountedPrice: number | null) => {
      updateContentData('discountedPrice', discountedPrice);
    },
    [updateContentData]
  );

  const updateDiscountPercent = useCallback(
    (discountPercent: number | null) => {
      updateContentData('discountPercent', discountPercent);
    },
    [updateContentData]
  );

  // Media Assets section update functions
  const addPhoto = useCallback(
    async (file: File, displayName: string) => {
      if (!contentUploader) {
        showErrorNotification('Please save the content item before adding uploads');
        throw new Error('ContentUploader not available in create mode');
      }

      try {
        const uploadResult = await contentUploader.uploadAndProcessFile({
          file,
          displayName,
          fileType: 'image',
        });

        if (uploadResult) {
          // uploadResult should contain the processed upload data from the backend
          const updatedPhotos = [...(contentEditState.photos || []), uploadResult];
          updateContentData('photos', updatedPhotos);
          showSuccessNotification('Image uploaded successfully');
        } else {
          showErrorNotification('Failed to upload image');
        }
      } catch (error) {
        showErrorNotification('Failed to upload image');
      }
    },
    [contentUploader, updateContentData, contentEditState.photos, showSuccessNotification, showErrorNotification]
  );

  const removePhoto = useCallback(
    (index: number) => {
      const updatedPhotos = (contentEditState.photos || []).filter((_, i) => i !== index);
      updateContentData('photos', updatedPhotos);
    },
    [updateContentData, contentEditState.photos]
  );

  // Supporting Documents section update functions
  const addDocument = useCallback(
    async (file: File, displayName: string) => {
      if (!contentUploader) {
        showErrorNotification('Please save the content item before adding uploads');
        throw new Error('ContentUploader not available in create mode');
      }

      try {
        const uploadResult = await contentUploader.uploadAndProcessFile({
          file,
          displayName,
          fileType: 'file',
        });

        if (uploadResult) {
          // uploadResult should contain the processed upload data from the backend
          const updatedDocuments = [...(contentEditState.documents || []), uploadResult];
          updateContentData('documents', updatedDocuments);
          showSuccessNotification('Document uploaded successfully');
        } else {
          showErrorNotification('Failed to upload document');
        }
      } catch (error) {
        showErrorNotification('Failed to upload document');
      }
    },
    [contentUploader, updateContentData, contentEditState.documents, showSuccessNotification, showErrorNotification]
  );

  const removeDocument = useCallback(
    (index: number) => {
      const updatedDocuments = (contentEditState.documents || []).filter((_, i) => i !== index);
      updateContentData('documents', updatedDocuments);
    },
    [updateContentData, contentEditState.documents]
  );

  // Publishing Settings section update functions
  const updateSaveAsDraft = useCallback(
    (saveAsDraft: boolean) => {
      updateContentData('saveAsDraft', saveAsDraft);
    },
    [updateContentData]
  );

  const updatePublishFor = useCallback(
    (publishFor: 'all_users' | 'internal_users_only' | 'external_users_only') => {
      updateContentData('publishFor', publishFor);
    },
    [updateContentData]
  );

  const updateActiveFrom = useCallback(
    (activeFrom: string) => {
      updateContentData('activeFrom', activeFrom);
      if (activeFrom.trim()) {
        clearValidationError('activeFrom');
      }
    },
    [updateContentData, clearValidationError]
  );

  const updateActiveTo = useCallback(
    (activeTo: string) => {
      updateContentData('activeTo', activeTo);
    },
    [updateContentData]
  );

  const updateContactEmail = useCallback(
    (contactEmail: string) => {
      updateContentData('contactEmail', contactEmail);
      if (contactEmail.trim() && isValidEmail(contactEmail.trim())) {
        clearValidationError('contactEmail');
      }
    },
    [updateContentData, clearValidationError]
  );

  return {
    contentEditState,
    isEditedWithoutSaving,
    isLoading: isLoading || isLoadingOpportunity,
    error: error || (opportunityError ? 'Failed to load content' : null),
    validationErrors,
    handleSave,
    handleCancel,
    updateContentData,
    updateTitle,
    // Main Info section update functions
    updateCrmCompanyUuid,
    updateCategoryUuid,
    updateCategoryEnabled,
    updateDestination,
    updateProductName,
    updateIsExclusive,
    updateProductTypeUuids,
    updateHeroImage,
    addHeroImage,
    // Content Details section update functions
    updateShortDescriptionTitle,
    updateShortDescriptionContent,
    addSection,
    removeSection,
    updateSectionTitle,
    updateSectionContent,
    // Availability & Booking section update functions
    updateCountryCodes,
    resetCountryCodes,
    updateBookingStart,
    updateBookingEnd,
    updateStayPeriodStart,
    updateStayPeriodEnd,
    // Pricing Info section update functions
    updateCurrency,
    updateFullPrice,
    updateDiscountedPrice,
    updateDiscountPercent,
    // Media Assets section update functions
    addPhoto,
    removePhoto,
    // Supporting Documents section update functions
    addDocument,
    removeDocument,
    // Publishing Settings section update functions
    updateSaveAsDraft,
    updatePublishFor,
    updateActiveFrom,
    updateActiveTo,
    updateContactEmail,
  };
};
